// Lab Issues — single-issue detail: pipeline state, source file, job
// history, actions.
//
// The four states have different owners (see LabIssues.tsx): missing_source
// needs a human to upload a PDF onto EFS (mounted read-only here, so no
// automated path can supply it — the render endpoint 409s, so no button is
// offered), awaiting_render resolves itself on the next drain or the 02:20
// UTC cron, and rendered issues only need attention if a corrected PDF
// landed after the last render (RE-RENDER).
//
// The page offers exactly ONE action, chosen by state: a rendered issue is
// skipped by bulk_ingest_issues.py unless --force, so a plain "render now"
// on one of those queued a job that rendered nothing.

import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../api/client'
import {
  ISSUE_STATUS_CLASS, ISSUE_STATUS_LABEL, issueActionError, usePublishIssue,
} from './usePublishIssue'
import { usePrintReady } from './usePrintReady'
import { MemberIssueLink } from './MemberPageLink'

const EFS_DIR: Record<string, string> = {
  flight_journal: 'flj',
  diecast: 'dcx',
  model_airplane_news: 'man',
  rc_car_action: 'rca',
}

// Job timestamps are stored and served as UTC (…Z) but read by operators
// in the Eastern office, and the render cron is reasoned about in local
// time. Formatting with a named zone rather than a fixed offset means the
// label says EDT in summer and EST in winter on its own — a hardcoded -5
// would be an hour wrong for two-thirds of the year.
const EASTERN = 'America/New_York'

function fmtEastern(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN,
    year: 'numeric', month: 'short', day: '2-digit',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
    timeZoneName: 'short',
  }).format(d)
}

type Job = {
  id: number
  action: string
  force: boolean
  status: string
  requested_by: string | null
  requested_at: string | null
  completed_at: string | null
  error: string | null
  log_tail: string | null
}

type Detail = {
  id: number
  publication_slug: string
  title: string | null
  post_date: string | null
  sku: string | null
  state: string
  viewer_url: string | null
  pages_base_url: string | null
  /** Signed CloudFront query appended to every page URL (the CDN refuses
      unsigned edition pages). */
  pages_query: string | null
  pdf_cdn_url: string | null
  /** Whether a printed copy can be produced. Distinct from `status`: an
   *  issue can be published and readable while its only PDF is a scan. */
  print_ready: boolean
  pdf_page_count: number | null
  rendered_at: string | null
  source_path: string | null
  source_size_bytes: number | null
  source_seen_at: string | null
  source_mtime: string | null
  // Publication state, distinct from `state` (the render pipeline): a fully
  // rendered issue can still be a draft nobody can read.
  status: string
  origin: string
  published_at: string | null
  published_by: string | null
  jobs: Job[]
}

// Release control. Members gate on status='active', so this one flip
// governs the issue list, search and the reader together.
function MembershipPanel({ d }: { d: Detail }) {
  const [confirming, setConfirming] = useState(false)
  const flip = usePublishIssue(d.id)
  const printReady = usePrintReady(d.id)
  useEffect(() => { if (flip.isSuccess) setConfirming(false) }, [flip.isSuccess])

  const navigate = useNavigate()
  // Creating the announcement emails can be refused (409) — e.g. the title
  // isn't open for member email yet; that message shares the error line.
  const [announceErr, setAnnounceErr] = useState<string | null>(null)
  const [announcing, setAnnouncing] = useState(false)

  const live = d.status === 'active'
  const err = issueActionError(flip.error) ?? announceErr
  // The API refuses this too; disabling explains it before the click
  // rather than after a failed one.
  const unrendered = !d.pages_base_url

  return (
    <section className="border border-ink-500 p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`font-mono text-[11px] tracking-label uppercase border px-2 py-0.5 ${
            ISSUE_STATUS_CLASS[d.status] ?? 'border-paper-trace text-paper-trace'
          }`}
        >
          {ISSUE_STATUS_LABEL[d.status] ?? d.status}
        </span>
        <span className="font-mono text-[11px] text-paper-trace">
          {live
            ? 'Visible to members.'
            : 'Not visible to members \u2014 hidden from the issue list, search and the reader.'}
        </span>
      </div>

      {live && d.published_at && (
        <div className="font-mono text-[10px] text-paper-trace border-l border-ink-500 pl-2">
          Published {fmtEastern(d.published_at)}
          {d.published_by ? ` by ${d.published_by}` : ''}
        </div>
      )}

      {/* A different question from publishing, and deliberately beside it
          rather than inside it: an issue can be published and readable
          while its only PDF is a scan nobody can print from. */}
      <div className="flex gap-2 items-center flex-wrap">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(d.print_ready)}
            disabled={printReady.isPending}
            onChange={(e) => printReady.mutate(e.target.checked)}
          />
          <span className="font-mono text-[10px] uppercase tracking-label">
            Can be printed
          </span>
        </label>
        <span className="font-mono text-[10px] text-paper-trace">
          {d.print_ready
            ? 'Offered in print to members and visitors.'
            : 'Not offered in print \u2014 nobody can buy a printed copy of this issue.'}
        </span>
        {!d.pdf_cdn_url && (
          <span className="font-mono text-[10px] text-amber-glow">
            No PDF on file, so there is nothing to print from.
          </span>
        )}
      </div>

      {!live && unrendered && (
        <div className="font-mono text-[10px] text-amber-glow">
          It has to render before it can be published \u2014 otherwise members
          would open an issue with no pages.
        </div>
      )}

      {err && <div className="font-mono text-[10px] text-radar-red">{err}</div>}

      {confirming ? (
        <div className="flex gap-2 items-center flex-wrap">
          <span className="font-mono text-[10px] text-amber-glow">
            Hide this issue from members?
          </span>
          <button
            type="button"
            onClick={() => flip.mutate(false)}
            disabled={flip.isPending}
            className="font-mono text-[11px] tracking-label uppercase border border-radar-red/60 text-radar-red px-2.5 py-1 hover:bg-radar-red/10 disabled:opacity-50"
          >
            {flip.isPending ? 'Working\u2026' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={flip.isPending}
            className="font-mono text-[11px] tracking-label uppercase text-paper-trace hover:text-paper disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => (live ? setConfirming(true) : flip.mutate(true))}
          disabled={flip.isPending || (!live && unrendered)}
          className={`font-mono text-[11px] tracking-label uppercase border px-2.5 py-1 disabled:opacity-30 ${
            live
              ? 'border-paper-trace text-paper-trace hover:text-paper hover:border-paper'
              : 'border-radar-green/60 text-radar-green hover:bg-radar-green/10'
          }`}
        >
          {flip.isPending ? 'Working\u2026' : live ? 'Unpublish' : 'Publish to members'}
        </button>
      )}

      {live && d.pages_base_url && (
        <Link
          to={`/issue-announcements/${d.id}`}
          aria-disabled={announcing}
          onClick={async (e) => {
            e.preventDefault()
            if (announcing) return
            setAnnounceErr(null)
            setAnnouncing(true)
            try {
              // Idempotent server-side: an issue that already has its emails just opens them.
              await api.post('/api/issue-announcements', { edition_id: d.id })
              navigate(`/issue-announcements/${d.id}`)
            } catch (error) {
              setAnnounceErr(issueActionError(error))
            } finally {
              setAnnouncing(false)
            }
          }}
          className="inline-block font-mono text-[11px] tracking-label uppercase border border-radar-green/60 text-radar-green px-2.5 py-1 hover:bg-radar-green/10"
        >
          {announcing ? 'Drafting\u2026' : 'Create announcement emails'}
        </Link>
      )}
    </section>
  )
}

export function LabIssueViewPage() {
  const { id } = useParams()
  const qc = useQueryClient()

  const detail = useQuery({
    queryKey: ['lab', 'issues', 'detail', id],
    queryFn: () => api.get<Detail>(`/api/lab/issues/${id}`),
    // While a job is in flight, poll so the operator sees it land.
    refetchInterval: (q) =>
      (q.state.data?.jobs ?? []).some(
        (j) => j.status === 'pending' || j.status === 'running',
      ) ? 5000 : false,
  })

  const render = useMutation({
    mutationFn: (force: boolean) =>
      api.post<Job>(`/api/lab/issues/${id}/render`, { force }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lab', 'issues'] }),
  })

  const d = detail.data
  if (!d) return <div className="p-8 label text-paper-trace">Loading…</div>

  const efsBase = `/mnt/efs/membership/magazines/allpdfs/${EFS_DIR[d.publication_slug] ?? '?'}/`

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <Link to="/lab/issues" className="label hover:text-amber-glow">← All issues</Link>
      <h1 className="font-serif text-[26px] text-paper flex items-center gap-3">
        {d.title}
        <MemberIssueLink
          publicationSlug={d.publication_slug}
          viewerUrl={d.viewer_url}
          status={d.status}
          rendered={Boolean(d.pages_base_url)}
        />
      </h1>

      <dl className="grid grid-cols-2 gap-3 font-mono text-[12px]">
        <dt className="label">SKU</dt><dd className="text-paper">{d.sku ?? '—'}</dd>
        <dt className="label">State</dt><dd className="text-paper">{d.state}</dd>
        <dt className="label">Pages</dt><dd className="text-paper">{d.pdf_page_count ?? '—'}</dd>
        <dt className="label">Source PDF</dt>
        <dd className="text-paper">{d.source_path ?? 'not on EFS'}</dd>
        <dt className="label">Source last seen</dt>
        <dd className="text-paper">{d.source_seen_at?.slice(0, 19) ?? '—'}</dd>
        <dt className="label">Source file mtime</dt>
        <dd className="text-paper">{d.source_mtime?.slice(0, 19) ?? '—'}</dd>
        <dt className="label">Pages rendered</dt>
        <dd className="text-paper">
          {d.rendered_at?.slice(0, 19) ?? (d.pages_base_url ? 'before 2026-08-20' : 'never')}
        </dd>
      </dl>
      {!d.rendered_at && d.pages_base_url && (
        <p className="text-paper-trace text-[12px]">
          Render timestamps are only tracked from this release — this issue
          was rendered before that, so the exact date isn't recorded.
        </p>
      )}

      {d.source_mtime && d.rendered_at && d.source_mtime > d.rendered_at && (
        <div className="border border-amber-glow/40 p-3 font-mono text-[12px] text-amber-glow">
          The PDF on EFS is newer than the rendered pages — a corrected file
          was probably uploaded. Use RE-RENDER to pick it up.
        </div>
      )}

      {d.state === 'missing_source' && (
        <div className="border border-red-400/40 p-4">
          <div className="font-serif text-[16px] text-paper mb-1">
            No PDF has been delivered for this issue
          </div>
          <p className="text-paper-dim text-[13px] mb-2">
            Rendering cannot run until the file exists. EFS is mounted
            read-only here, so it has to be uploaded by whoever handles issue
            delivery.
          </p>
          {d.sku ? (
            <>
              <p className="text-paper-dim text-[13px] mb-2">
                Expected location:
              </p>
              <code className="font-mono text-[12px] text-amber-glow">
                {efsBase}{d.post_date ? `${d.post_date.slice(0, 4)}/` : '<year>/'}{d.sku}.pdf
              </code>
              {!d.post_date && (
                <p className="text-paper-trace text-[12px] mt-1">
                  Post date is unknown, so the year subfolder above (
                  <code>&lt;year&gt;</code>) can't be filled in — check the
                  directory for the file directly.
                </p>
              )}
            </>
          ) : (
            <p className="text-amber-glow font-mono text-[12px]">
              This issue has no SKU, so the expected filename is unknown.
              The SKU could not be derived from the WordPress title — that
              title needs attention before this file can be located or
              uploaded.
            </p>
          )}
          <p className="text-paper-trace text-[12px] mt-2">
            Once it lands, the 02:20 UTC run picks it up automatically — or
            come back here and render it immediately.
          </p>
        </div>
      )}

      <MembershipPanel d={d} />

      {/* One action, chosen by this issue's own state. A rendered issue
          needs --force or bulk_ingest_issues.py skips it and the job
          renders nothing, so offering a plain RENDER NOW alongside
          RE-RENDER was offering a button that did nothing. */}
      <div className="flex flex-wrap gap-3 items-start">
        {d.sku === null ? (
          <p className="text-paper-trace font-mono text-[12px]">
            No SKU, so a render cannot be targeted at this issue — fix the
            WordPress title first.
          </p>
        ) : d.state === 'missing_source' ? null : d.state === 'rendered' ? (
          <button
            type="button"
            disabled={render.isPending}
            onClick={() => render.mutate(true)}
            className="px-4 py-2 border border-paper-trace text-paper font-mono text-[12px] tracking-label disabled:opacity-30"
          >
            {render.isPending ? 'QUEUEING…' : 'RE-RENDER'}
          </button>
        ) : (
          <button
            type="button"
            disabled={render.isPending}
            onClick={() => render.mutate(false)}
            className="px-4 py-2 bg-amber-glow text-paper-deepest font-mono text-[12px] tracking-label disabled:opacity-30"
          >
            {render.isPending ? 'QUEUEING…' : 'RENDER NOW'}
          </button>
        )}

        {/* Collapsed by default: a rendered issue's log tail runs to
            hundreds of lines, and it is reference material for when
            something looks wrong, not part of reading the page. Native
            <details> so it keeps keyboard and screen-reader behaviour
            without a state hook. */}
        <details className="flex-1 min-w-[280px] border border-ink-500/60">
          <summary className="cursor-pointer select-none px-3 py-2 font-mono text-[11px] tracking-label uppercase text-paper-dim hover:text-paper">
            Job history ({d.jobs.length})
          </summary>
          <div className="px-3 pb-3">
            {d.jobs.length === 0 ? (
              <div className="label">No jobs yet.</div>
            ) : (
              <ul className="space-y-3">
                {d.jobs.map((j) => (
                  <li key={j.id} className="border-b border-ink-500/40 pb-2">
                    <div className="font-mono text-[11px] text-paper-dim">
                      #{j.id} · {j.action}{j.force ? ' (force)' : ''} · {j.status}
                      {j.requested_by ? ` · ${j.requested_by}` : ''}
                    </div>
                    <div className="font-mono text-[10px] text-paper-trace mt-0.5">
                      {fmtEastern(j.requested_at)
                        ? `Requested ${fmtEastern(j.requested_at)}`
                        : 'Requested — unknown'}
                      {fmtEastern(j.completed_at)
                        && ` · Completed ${fmtEastern(j.completed_at)}`}
                    </div>
                    {j.error && (
                      <div className="font-mono text-[11px] text-red-400">{j.error}</div>
                    )}
                    {j.log_tail && (
                      <pre className="font-mono text-[10px] text-paper-trace whitespace-pre-wrap mt-1 overflow-x-auto">
                        {j.log_tail.slice(-600)}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </details>
      </div>

      {render.isError && (
        <div className="text-red-400 font-mono text-[12px]">
          {(() => {
            const e = render.error
            if (e instanceof ApiError) {
              // e.detail is the whole response body; FastAPI serializes
              // HTTPException(detail={...}) as {"detail": {...}}, so the
              // structured message is one level deeper than e.detail itself.
              const det = e.detail as { detail?: { message?: string } } | undefined
              return det?.detail?.message ?? `Error ${e.status}`
            }
            return 'Request failed'
          })()}
        </div>
      )}

      <ContentsPanel editionId={d.id} />

      {d.pages_base_url && d.pdf_page_count ? (
        <PageGrid
          editionId={d.id}
          baseUrl={d.pages_base_url}
          pagesQuery={d.pages_query}
          pageCount={d.pdf_page_count}
          sku={d.sku ?? `issue-${d.id}`}
        />
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------
// Contents links
// ---------------------------------------------------------------------
// Which web article each Contents line opens in the reader. Lines link
// automatically (the Desk piece placed on the line, else a title match,
// hourly); a pick here overrides that until handed back to Automatic.

type ContentsLine = {
  id: number
  page: number | null
  section: string | null
  title: string
  article_id: number | null
  article_title: string | null
  how: 'hand' | 'piece' | 'name' | null
}
type Contents = { lines: ContentsLine[]; articles: { id: number; title: string | null; slug: string | null }[] }

const HOW: Record<string, string> = {
  hand: 'Set by hand',
  piece: 'From its Desk piece',
  name: 'Matched by title',
}

function ContentsPanel({ editionId }: { editionId: number }) {
  const qc = useQueryClient()
  const key = ['lab', 'issues', 'contents', editionId]
  const q = useQuery({ queryKey: key, queryFn: () => api.get<Contents>(`/api/lab/issues/${editionId}/contents`) })
  const save = useMutation({
    mutationFn: ({ tocId, value }: { tocId: number; value: string }) =>
      api.put<Contents>(`/api/lab/issues/${editionId}/contents/${tocId}`,
        value === 'auto' ? { automatic: true } : { article_id: value === 'none' ? null : Number(value) }),
    onSuccess: (data) => qc.setQueryData(key, data),
  })
  if (!q.data) return null
  const { lines, articles } = q.data
  const linked = lines.filter((l) => l.article_id != null).length
  return (
    <section className="border border-ink-500 p-4 space-y-3">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="font-serif text-[18px] text-paper">Contents links</h2>
        <span className="font-mono text-[11px] text-paper-trace">
          {lines.length === 0
            ? 'No Contents yet: it is read from the issue after rendering (and nightly).'
            : `${linked} of ${lines.length} lines open a web article; the others turn to the page.`}
        </span>
      </div>
      {lines.length > 0 && (
        <ul className="space-y-2">
          {lines.map((l) => {
            const value = l.how === 'hand' ? (l.article_id == null ? 'none' : String(l.article_id)) : 'auto'
            return (
              <li key={l.id} className="flex flex-wrap items-center gap-3 border-b border-ink-500/40 pb-2">
                <span className="w-12 font-mono text-[11px] text-paper-trace">{l.page != null ? `p. ${l.page}` : ''}</span>
                <span className="min-w-[200px] flex-1 font-ui text-[14px] text-paper">{l.title}</span>
                <select
                  aria-label={`Article for ${l.title}`}
                  value={value}
                  disabled={save.isPending}
                  onChange={(e) => save.mutate({ tocId: l.id, value: e.target.value })}
                  className="max-w-[420px] border border-ink-400 bg-ink-800 px-2 py-1 font-ui text-[13px] text-paper"
                >
                  <option value="auto">
                    Automatic{l.how !== 'hand' ? (l.article_title ? `: ${l.article_title}` : ': no match') : ''}
                  </option>
                  <option value="none">No article (turn to the page)</option>
                  {articles.map((a) => <option key={a.id} value={a.id}>{a.title ?? a.slug ?? `#${a.id}`}</option>)}
                </select>
                <span className="w-36 font-mono text-[10px] text-paper-trace">{l.how ? HOW[l.how] : 'Not linked'}</span>
              </li>
            )
          })}
        </ul>
      )}
      {save.isError && (
        <div className="font-mono text-[12px] text-red-400">
          Could not save the link. {save.error instanceof ApiError ? `Error ${save.error.status}` : 'Request failed'}
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------
// Page grid
// ---------------------------------------------------------------------
// Renders every page of the issue straight from the CDN. There are no
// thumbnail derivatives — the pipeline only emits full-size 150-DPI JPEGs
// (~341 KB each, ~92 pages for a typical issue), so `loading="lazy"` is
// load-bearing, not a nicety: without it, opening a detail page would pull
// ~31 MB. Each tile keeps a fixed aspect ratio so a 284-page grid doesn't
// reflow as images arrive.
//
// URL shape matches the reader-app's flip viewer: <base>/page-001.jpg.

function pageUrl(baseUrl: string, n: number, query: string | null): string {
  const url = `${baseUrl}/page-${String(n).padStart(3, '0')}.jpg`
  return query ? `${url}?${query}` : url
}

/** Save a page under an operator-chosen filename.
 *
 * Fetches through the SAME-ORIGIN proxy (/api/lab/issues/:id/page/:n) rather
 * than the CDN directly. A cross-origin fetch needs CORS, and only
 * cdn.flightjournal.com sends it — MAN, RCCA and DCX send none (verified
 * 2026-08-20), so a direct fetch failed for three of the four titles and the
 * browser just opened the raw JPEG instead of downloading it.
 *
 * The <img> tags in the grid still load straight from the CDN; only this
 * download path goes through the API.
 */
async function downloadPage(
  editionId: number, page: number, filename: string,
): Promise<void> {
  const res = await fetch(`/api/lab/issues/${editionId}/page/${page}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error(`page fetch failed (${res.status})`)
  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objectUrl)
}

function PageGrid({
  editionId, baseUrl, pagesQuery, pageCount, sku,
}: {
  editionId: number
  baseUrl: string
  pagesQuery: string | null
  pageCount: number
  sku: string
}) {
  const [openPage, setOpenPage] = useState<number | null>(null)
  // Rename-before-download: holds the page being saved and the editable name.
  const [pending, setPending] = useState<{ page: number; name: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1)

  const nameFor = (n: number) => `${sku}-page-${String(n).padStart(3, '0')}.jpg`

  // Arrow keys / Esc while the lightbox is open.
  useEffect(() => {
    if (openPage === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenPage(null)
      else if (e.key === 'ArrowRight') setOpenPage((p) => (p && p < pageCount ? p + 1 : p))
      else if (e.key === 'ArrowLeft') setOpenPage((p) => (p && p > 1 ? p - 1 : p))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openPage, pageCount])

  const save = async () => {
    if (!pending) return
    const name = pending.name.trim()
    if (!name) return
    setSaving(true)
    setSaveError(null)
    try {
      await downloadPage(editionId, pending.page, name)
      setPending(null)
    } catch (err) {
      // Surface the reason rather than silently doing nothing — the previous
      // version fell back to opening the image in a tab, which looked like
      // the download had simply failed to fire.
      setSaveError(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-serif text-[18px] text-paper">Pages</h2>
        <span className="label">{pageCount} pages · click to enlarge</span>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {pages.map((n) => (
          <div key={n} className="relative">
            <button
              type="button"
              onClick={() => setOpenPage(n)}
              className="block w-full border border-paper-trace hover:border-amber-glow transition-colors"
              style={{ aspectRatio: '17 / 22' }}
              aria-label={`Page ${n} — click to enlarge`}
            >
              <img
                src={pageUrl(baseUrl, n, pagesQuery)}
                alt={`Page ${n}`}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover bg-paper-deepest"
              />
            </button>
            {/* Download stays visible. It was hover-revealed at first and
                nobody found it — a 10px arrow that appears on hover is the
                same as no affordance at all. */}
            <div className="flex items-center justify-between mt-1">
              <span className="font-mono text-[11px] text-paper-dim tabular-nums">
                p{n}
              </span>
              <button
                type="button"
                onClick={() => setPending({ page: n, name: nameFor(n) })}
                className="font-mono text-[11px] text-amber-glow/70 hover:text-amber-glow hover:underline"
                aria-label={`Download page ${n}`}
                title={`Download ${nameFor(n)}`}
              >
                ↓ JPG
              </button>
            </div>
          </div>
        ))}
      </div>

      {pending && (
        <div
          className="fixed inset-0 z-[60] bg-ink-900/95 flex items-center justify-center p-6"
          onClick={() => !saving && setPending(null)}
        >
          <div
            className="border border-paper-trace bg-paper-deepest p-5 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-[18px] text-paper mb-1">
              Download page {pending.page}
            </h3>
            <p className="label mb-3">Change the filename if you like.</p>
            <input
              autoFocus
              value={pending.name}
              onChange={(e) => setPending({ ...pending, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void save()
                else if (e.key === 'Escape' && !saving) setPending(null)
              }}
              className="w-full bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper"
              aria-label="Filename"
            />
            {saveError && (
              <div className="font-mono text-[11px] text-red-400 mt-2">{saveError}</div>
            )}
            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                disabled={saving}
                onClick={() => setPending(null)}
                className="font-mono text-[11px] text-paper-dim hover:text-paper disabled:opacity-30"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving || !pending.name.trim()}
                onClick={() => void save()}
                className="px-4 py-2 bg-amber-glow text-paper-deepest font-mono text-[11px] tracking-label disabled:opacity-30"
              >
                {saving ? 'SAVING…' : 'DOWNLOAD'}
              </button>
            </div>
          </div>
        </div>
      )}

      {openPage !== null && (
        <div
          className="fixed inset-0 z-50 bg-ink-900/95 flex flex-col"
          onClick={() => setOpenPage(null)}
        >
          <div
            className="flex items-center justify-between px-5 py-3 border-b border-ink-500"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="font-mono text-[12px] text-paper">
              {sku} · page {openPage} of {pageCount}
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={openPage <= 1}
                onClick={() => setOpenPage((p) => (p && p > 1 ? p - 1 : p))}
                className="btn-secondary font-mono text-[11px] disabled:opacity-30"
              >
                ‹ Prev
              </button>
              <button
                type="button"
                disabled={openPage >= pageCount}
                onClick={() => setOpenPage((p) => (p && p < pageCount ? p + 1 : p))}
                className="btn-secondary font-mono text-[11px] disabled:opacity-30"
              >
                Next ›
              </button>
              <button
                type="button"
                onClick={() => setPending({ page: openPage, name: nameFor(openPage) })}
                className="font-mono text-[11px] text-amber-glow hover:underline"
              >
                Download
              </button>
              <button
                type="button"
                onClick={() => setOpenPage(null)}
                className="font-mono text-[11px] text-paper-dim hover:text-paper"
              >
                Close ✕
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-5 flex items-start justify-center">
            <img
              src={pageUrl(baseUrl, openPage, pagesQuery)}
              alt={`Page ${openPage}`}
              className="max-w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </section>
  )
}
